import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CalendarTooltip } from "@/components/CalendarTooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { isToday as isTodayFn } from "date-fns";
import { Lock, TrendingUp, ArrowUp, Star, Clock, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Type definitions
export type Volatility = 'low' | 'medium' | 'high';

interface Event {
  id: string;
  title: string;
  date: string;
  impact: string;
  score: number;
}

export interface EventInfo {
  count: number;
  value: number;
  threshold?: number;
  volatility?: Volatility;
  totalScore?: number;
  dailySum?: number;
  majorEventTime?: string;
  hasHighImpactEvents?: boolean;
}

export interface CalendarDayProps {
  date: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  events?: EventInfo;
  onClick?: (date: number) => void;
  disabled?: boolean;
  currentMonth: Date;
  isLoading?: boolean;
  isLastMonth?: boolean;
}

// Enhanced helper functions for modern heatmap visualization
export const getVolatilityColor = (volatility?: Volatility): string => {
  if (!volatility) return "bg-slate-600/30";
  switch (volatility) {
    case 'high':
      return "bg-gradient-to-br from-purple-500 via-purple-600 to-violet-700";
    case 'medium':
      return "bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600";
    case 'low':
      return "bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600";
    default:
      return "bg-slate-600/30";
  }
};

export const getVolatilityTextColor = (volatility?: Volatility): string => {
  if (!volatility) return "text-slate-400";
  switch (volatility) {
    case 'high':
      return "text-purple-400";
    case 'medium':
      return "text-blue-400";
    case 'low':
      return "text-cyan-400";
    default:
      return "text-slate-400";
  }
};

export const getVolatilityBackground = (volatility?: Volatility, intensity: number = 1): string => {
  if (!volatility) return "bg-transparent";
  
  const baseOpacity = Math.min(intensity * 0.15, 0.4);
  const pulseOpacity = Math.min(intensity * 0.25, 0.6);
  
  switch (volatility) {
    case 'high':
      return `bg-gradient-to-br from-purple-500/${Math.round(baseOpacity * 100)} via-purple-600/${Math.round(baseOpacity * 100)} to-violet-700/${Math.round(baseOpacity * 100)}`;
    case 'medium':
      return `bg-gradient-to-br from-blue-500/${Math.round(baseOpacity * 80)} via-blue-600/${Math.round(baseOpacity * 80)} to-purple-600/${Math.round(baseOpacity * 80)}`;
    case 'low':
      return `bg-gradient-to-br from-cyan-500/${Math.round(baseOpacity * 60)} via-blue-500/${Math.round(baseOpacity * 60)} to-blue-600/${Math.round(baseOpacity * 60)}`;
    default:
      return "bg-transparent";
  }
};

export const getVolatilityGlow = (volatility?: Volatility, intensity: number = 1): string => {
  if (!volatility) return "";
  
  const glowIntensity = Math.min(intensity, 2);
  
  switch (volatility) {
    case 'high':
      return `shadow-lg shadow-purple-500/${Math.round(glowIntensity * 20)} hover:shadow-xl hover:shadow-purple-500/${Math.round(glowIntensity * 30)}`;
    case 'medium':
      return `shadow-md shadow-blue-500/${Math.round(glowIntensity * 15)} hover:shadow-lg hover:shadow-blue-500/${Math.round(glowIntensity * 25)}`;
    case 'low':
      return `shadow-sm shadow-cyan-500/${Math.round(glowIntensity * 10)} hover:shadow-md hover:shadow-cyan-500/${Math.round(glowIntensity * 20)}`;
    default:
      return "";
  }
};

/**
 * CalendarDay Component
 * Clean, mobile-first design with progressive disclosure
 */
export const CalendarDay = ({
  date,
  isCurrentMonth,
  isToday,
  events,
  onClick,
  disabled,
  currentMonth,
  isLoading = false,
  isLastMonth
}: CalendarDayProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="h-12 sm:h-16 md:h-20 lg:h-24 bg-slate-800/20 border-b border-r border-slate-700/30 p-2">
        <Skeleton className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-slate-700/50" />
        <div className="mt-1 space-y-1">
          <Skeleton className="w-full h-1 rounded-sm bg-slate-700/30" />
        </div>
      </div>
    );
  }

  // Extract event properties with defaults for cleaner JSX
  const volatility = events?.volatility || 'low';
  const hasEvents = events && events.count > 0;
  const eventCount = events?.count || 0;
  const volatilityScore = events?.value || 0;
  const isHighImpact = hasEvents && volatility === 'high';
  
  // Calculate heat intensity based on score and threshold
  const heatIntensity = events?.threshold ? Math.min(volatilityScore / events.threshold, 2.5) : 1;

  // Event handlers
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(date);
    }
  };

  // Helper functions that need component props
  const isWeekend = () => {
    const dayOfWeek = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      date
    ).getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Simplified background based on events - make more prominent for heatmap effect
  const getEventBackground = () => {
    if (!hasEvents || disabled) return "";
    
    // Enhanced opacity calculation based on volatility score for better visual hierarchy
    const scoreBasedOpacity = Math.min(volatilityScore / 600.0, 1.0); // Scale based on max score of 600
    const baseOpacity = Math.max(0.4, Math.min(scoreBasedOpacity * 0.8 + 0.2, 0.95)); // Ensure minimum visibility
    
    switch (volatility) {
      case 'high':
        return `bg-gradient-to-br from-purple-500/${Math.round(baseOpacity * 90)} via-purple-600/${Math.round(baseOpacity * 95)} to-violet-700/${Math.round(baseOpacity * 100)}`;
      case 'medium':
        return `bg-gradient-to-br from-blue-500/${Math.round(baseOpacity * 85)} via-indigo-500/${Math.round(baseOpacity * 90)} to-purple-500/${Math.round(baseOpacity * 85)}`;
      case 'low':
        return `bg-gradient-to-br from-cyan-400/${Math.round(baseOpacity * 75)} via-blue-500/${Math.round(baseOpacity * 80)} to-indigo-500/${Math.round(baseOpacity * 75)}`;
      default:
        return "";
    }
  };

  // Enhanced volatility dot with score-aware colors and effects
  const getVolatilityDotColor = () => {
    if (!hasEvents) return "";
    
    // Intensity based on score for more dynamic visual feedback
    const intensity = Math.min(volatilityScore / 4.0, 1.0);
    const ringOpacity = Math.round(intensity * 80 + 20); // 20-100% ring opacity
    const shadowOpacity = Math.round(intensity * 50 + 10); // 10-60% shadow opacity
    
    switch (volatility) {
      case 'high':
        return `bg-gradient-to-br from-purple-400 to-violet-600 ring-2 ring-purple-300/${ringOpacity} shadow-lg shadow-purple-500/${shadowOpacity}`;
      case 'medium':
        return `bg-gradient-to-br from-blue-400 to-indigo-600 ring-2 ring-blue-300/${ringOpacity} shadow-md shadow-blue-500/${shadowOpacity}`;
      case 'low':
        return `bg-gradient-to-br from-cyan-400 to-blue-500 ring-2 ring-cyan-300/${ringOpacity} shadow-sm shadow-cyan-500/${shadowOpacity}`;
      default:
        return "bg-slate-500";
    }
  };

  // Enhanced glow effects for high-impact days
  const getHighImpactGlow = () => {
    if (!isHighImpact || disabled) return "";
    return "shadow-xl shadow-purple-500/30 ring-1 ring-purple-400/30";
  };

  // Generate class names for the day cell with enhanced 2025 styling
  const dayCellClassNames = cn(
    // Base layout and sizing - mobile-first approach
    "relative group transition-all duration-300 ease-out transform-gpu",
    "h-12 sm:h-16 md:h-20 lg:h-24",
    "border-b border-r border-slate-700/40",
    "overflow-hidden",
    
    // Enhanced 2025 background with sophisticated gradients
    "bg-gradient-to-br from-slate-800/30 via-slate-900/40 to-black/50",
    "backdrop-blur-sm",
    
    // Month and disabled states with better visual feedback
    (!isCurrentMonth || isLastMonth) && "opacity-40 bg-slate-900/60",
    disabled && "opacity-25 bg-slate-900/80 grayscale",
    
    // Cursor pointer for all clickable dates (not just current month)
    !disabled && "cursor-pointer",
    
    // Interactive hover effects for current month days with enhanced effects
    !disabled && isCurrentMonth && [
      "hover:bg-slate-700/30 hover:border-slate-600/60",
      "hover:scale-[1.02] hover:shadow-lg",
      "active:scale-[0.98]",
      "hover:z-10",
    ],
    
    // Event-based background with enhanced gradients
    hasEvents && !disabled && getEventBackground(),
    
    // High impact special effects
    isHighImpact && !disabled && [
      getHighImpactGlow(),
      "animate-pulse-subtle"
    ],
    
    // Weekend styling - subtle and professional
    isWeekend() && "opacity-80",
    
    // Corner rounding for last month cell
    isLastMonth && "rounded-bl-lg lg:rounded-bl-xl",
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div onClick={handleClick} className={dayCellClassNames}>
          {/* Disabled state overlay */}
          {disabled && (
            <div className="absolute inset-0 bg-slate-800/40 backdrop-blur-[1px] flex items-center justify-center z-20">
              <Lock className="h-3 w-3 md:h-4 md:w-4 text-slate-400/70" />
            </div>
          )}

          {/* Enhanced high impact glow for better visibility */}
          {isHighImpact && !disabled && (
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/15 to-purple-600/20 opacity-80" />
          )}

          <div className="relative p-2 sm:p-3 h-full flex flex-col justify-between">
            {/* Date display - enhanced with better readability and contrast */}
            <div 
              className={cn(
                "flex items-center justify-center rounded-full font-bold transition-all duration-300",
                "w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8",
                "text-sm sm:text-base md:text-lg",
                "border border-transparent backdrop-blur-sm",
                "relative z-10", // Ensure date is always on top
                
                // Today styling - always prominent
                isToday && [
                  "bg-gradient-to-br from-blue-500 to-blue-700 border-blue-300/60",
                  "text-white shadow-xl shadow-blue-500/50",
                  "ring-2 ring-blue-400/60",
                  "scale-110 animate-pulse-glow"
                ],
                
                // Not current month - always visible with stronger background
                !isToday && !isCurrentMonth && "text-slate-400 bg-slate-800/90 border border-slate-700/70",
                
                // Current month weekdays - always visible with stronger background
                !isToday && isCurrentMonth && !isWeekend() && "text-white bg-black/90 border border-white/30 shadow-xl backdrop-blur-md",
                
                // Current month weekends - always visible with stronger background
                !isToday && isCurrentMonth && isWeekend() && "text-slate-200 bg-black/80 border border-slate-300/30 shadow-lg backdrop-blur-md",
              )}
            >
              {date}
            </div>

            {/* Enhanced event indicators - spark chart style volatility visualization */}
            {hasEvents && !disabled && (
              <div className="flex items-end justify-end gap-0.5">
                {/* Spark chart style bars based on volatility score */}
                <div className="flex items-end gap-0.5">
                  {/* Generate bars based on volatility level for clearer visual distinction */}
                  {Array.from({ 
                    length: volatilityScore < 200 ? 2 :     // Low volatility: 2 bars (< 200)
                            volatilityScore < 350 ? 3 :     // Medium volatility: 3 bars (200-349)  
                            volatilityScore < 500 ? 4 : 5   // High volatility: 4-5 bars (350+)
                  }).map((_, index) => {
                    // Progressive bar heights that represent the score intensity
                    const maxHeight = 16;
                    const baseHeight = 3;
                    // Normalize score to 0-1 range for height calculation
                    const normalizedScore = Math.min(volatilityScore / 600, 1.0);
                    const progressiveHeight = baseHeight + normalizedScore * (maxHeight - baseHeight) * ((index + 1) / 4);
                    const barHeight = Math.max(baseHeight, Math.min(maxHeight, progressiveHeight));
                    
                    // Opacity decreases for bars beyond the score level
                    const normalizedLevel = volatilityScore / 100; // Convert to 1-6 range for comparison
                    const isActiveBar = index < Math.ceil(normalizedLevel);
                    const opacity = isActiveBar ? 
                      Math.max(0.7, Math.min(1.0, normalizedScore + 0.3)) : 
                      0.2;
                    
                    return (
                      <div
                        key={index}
                        className={cn(
                          "w-0.5 sm:w-1 rounded-t-sm transition-all duration-300",
                          volatility === 'high' && "bg-purple-400",
                          volatility === 'medium' && "bg-blue-400", 
                          volatility === 'low' && "bg-cyan-400",
                          "group-hover:scale-110"
                        )}
                        style={{ 
                          height: `${barHeight}px`,
                          opacity: opacity
                        }}
                      />
                    );
                  })}
                </div>

                {/* Score display with better readability */}
                {hasEvents && (
                  <div className={cn(
                    "ml-1 px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-bold",
                    "bg-black/70 text-white border border-white/20",
                    "shadow-lg backdrop-blur-sm"
                  )}>
                    {Math.round(volatilityScore)}
                  </div>
                )}

                {/* High intensity pulse indicator for very high scores */}
                {volatilityScore >= 550 && (
                  <div className="absolute -top-0.5 -right-0.5">
                    <div className={cn(
                      "w-2 h-2 rounded-full animate-ping",
                      volatility === 'high' && "bg-purple-400",
                      volatility === 'medium' && "bg-blue-400",
                      volatility === 'low' && "bg-cyan-400"
                    )} />
                  </div>
                )}
              </div>
            )}

            {/* Premium HOT indicator - enhanced for very high impact */}
            {isHighImpact && events?.hasHighImpactEvents && !disabled && (
              <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-md text-[8px] font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/40 animate-pulse-glow">
                🔥 HOT
              </div>
            )}

            {/* Event count indicator for days with multiple events */}
            {hasEvents && eventCount > 3 && !disabled && (
              <div className="absolute top-1 left-1 px-1 py-0.5 rounded text-[8px] font-medium bg-slate-800/80 text-slate-300 border border-slate-600/50">
                {eventCount}
              </div>
            )}
          </div>
          
          {/* Enhanced hover and interaction effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/1 to-white/3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none" />
          
          {/* Subtle border glow on hover for event days */}
          {hasEvents && !disabled && (
            <div className="absolute inset-0 border border-transparent group-hover:border-white/10 rounded transition-all duration-300 pointer-events-none" />
          )}
          
          {/* Enhanced depth shadow for high-impact days */}
          {isHighImpact && !disabled && (
            <div className="absolute inset-0 rounded shadow-inner shadow-purple-500/10 pointer-events-none" />
          )}
        </div>
      </TooltipTrigger>
      
      {/* Enhanced tooltip content - now more important since we show less by default */}
      {hasEvents && !disabled && (
        <TooltipContent 
          side="top" 
          className="bg-slate-900/95 backdrop-blur-md border border-slate-700/50 p-0 shadow-2xl shadow-black/30"
        >
          <CalendarTooltip
            value={events.value}
            count={events.count}
            totalScore={events.totalScore}
            dailySum={events.dailySum}
            volatility={events.volatility}
          />
        </TooltipContent>
      )}
    </Tooltip>
  );
};
